import React from 'react';
import { Helmet } from 'react-helmet';
import '../../styles/theme.css';

const Terms = () => {
  return (
    <>
      <Helmet>
        <title>Terms of Service | OptiBill Analytics</title>
        <meta name="description" content="OptiBill Analytics Terms of Service - Legal terms and conditions governing the use of our medical billing and revenue cycle management services." />
        <meta name="robots" content="index, follow" />
      </Helmet>
      
      <div className="legal-shell">
        <div className="legal-container">
          <div className="legal-card">
            {/* Header */}
            <div className="legal-header">
              <h1 className="text-navy fw-bold" style={{ fontSize: 36, marginBottom: 12 }}>Terms of Service</h1>
              <p style={{ color: '#5A6478', fontSize: 16 }}>Last Updated: March 21, 2026</p>
            </div>

            {/* Introduction */}
            <section className="legal-section">
              <p style={{ color: '#5A6478', fontSize: 17, lineHeight: 1.7, marginBottom: 16 }}>
                Welcome to OptiBill Analytics. These Terms of Service ("Terms") govern your access to and use of our website, services, and products (collectively, the "Services"). By accessing or using our Services, you agree to be bound by these Terms.
              </p>
              <p style={{ color: '#5A6478', fontSize: 17, lineHeight: 1.7 }}>
                <strong>PLEASE READ THESE TERMS CAREFULLY BEFORE USING OUR SERVICES.</strong> If you do not agree to these Terms, you may not access or use our Services.
              </p>
            </section>

            {/* Section 1 */}
            <section style={{ marginBottom: 32 }}>
              <h2 className="text-navy fw-semibold" style={{ fontSize: 24, marginBottom: 16 }}>1. Acceptance of Terms</h2>
              <p style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7, marginBottom: 12 }}>
                By creating an account, accessing our website, or using our medical billing and revenue cycle management services, you:
              </p>
              <ul style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7, marginLeft: 24 }}>
                <li>Acknowledge that you have read and understood these Terms</li>
                <li>Agree to be bound by these Terms and our Privacy Policy</li>
                <li>Represent that you have the authority to enter into this agreement on behalf of your organization</li>
                <li>Confirm that you are at least 18 years of age</li>
                <li>Agree to comply with all applicable laws and regulations</li>
              </ul>
            </section>

            {/* Section 2 */}
            <section style={{ marginBottom: 32 }}>
              <h2 className="text-navy fw-semibold" style={{ fontSize: 24, marginBottom: 16 }}>2. Services Description</h2>
              <p style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7, marginBottom: 12 }}>
                OptiBill Analytics provides comprehensive medical billing and revenue cycle management services, including but not limited to:
              </p>
              <ul style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7, marginLeft: 24, marginBottom: 16 }}>
                <li>Claims submission and management</li>
                <li>Payment posting and reconciliation</li>
                <li>Denial management and appeals</li>
                <li>Accounts receivable follow-up</li>
                <li>Revenue cycle analytics and reporting</li>
                <li>Insurance verification and eligibility checks</li>
                <li>Credentialing support</li>
                <li>Compliance monitoring and audit support</li>
              </ul>
              <p style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7 }}>
                The specific services provided to you will be outlined in your Service Agreement or Statement of Work. We reserve the right to modify, suspend, or discontinue any aspect of our Services at any time with reasonable notice.
              </p>
            </section>

            {/* Section 3 */}
            <section style={{ marginBottom: 32 }}>
              <h2 className="text-navy fw-semibold" style={{ fontSize: 24, marginBottom: 16 }}>3. Service Agreement and Fees</h2>
              <h3 className="text-navy fw-semibold" style={{ fontSize: 20, marginBottom: 12, marginTop: 20 }}>3.1 Service Agreement</h3>
              <p style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7, marginBottom: 16 }}>
                Your use of our Services is governed by these Terms and any Service Agreement or Statement of Work executed between you and OptiBill Analytics. In the event of a conflict, the Service Agreement shall take precedence over these Terms.
              </p>

              <h3 className="text-navy fw-semibold" style={{ fontSize: 20, marginBottom: 12, marginTop: 20 }}>3.2 Fees and Payment</h3>
              <ul style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7, marginLeft: 24 }}>
                <li>Fees for our Services will be specified in your Service Agreement</li>
                <li>Payment terms are Net 30 days unless otherwise agreed</li>
                <li>All fees are non-refundable unless otherwise stated in writing</li>
                <li>Late payments may incur interest charges at 1.5% per month or the maximum rate permitted by applicable law, whichever is lower</li>
                <li>You are responsible for all applicable taxes, except those based on our net income</li>
                <li>We reserve the right to adjust fees with 60 days' written notice</li>
              </ul>
            </section>

            {/* Section 4 */}
            <section style={{ marginBottom: 32 }}>
              <h2 className="text-navy fw-semibold" style={{ fontSize: 24, marginBottom: 16 }}>4. Client Responsibilities</h2>
              <p style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7, marginBottom: 12 }}>
                To enable us to provide our Services effectively, you agree to:
              </p>
              <ul style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7, marginLeft: 24 }}>
                <li>Provide accurate, complete, and timely information necessary for billing and claims processing</li>
                <li>Maintain valid licensure, certifications, and provider credentials</li>
                <li>Ensure your practice management system and EHR are compatible with our processes</li>
                <li>Grant necessary access to systems and data required for our Services</li>
                <li>Respond promptly to requests for information or clarification</li>
                <li>Comply with all applicable healthcare laws, regulations, and payer requirements</li>
                <li>Review and approve reports, claims, and communications before submission</li>
                <li>Maintain appropriate professional liability insurance</li>
              </ul>
            </section>

            {/* Section 5 */}
            <section style={{ marginBottom: 32 }}>
              <h2 className="text-navy fw-semibold" style={{ fontSize: 24, marginBottom: 16 }}>5. Confidentiality and Data Protection</h2>
              <h3 className="text-navy fw-semibold" style={{ fontSize: 20, marginBottom: 12, marginTop: 20 }}>5.1 Confidential Information</h3>
              <p style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7, marginBottom: 16 }}>
                We acknowledge that in providing our Services, we may have access to confidential information, including Protected Health Information (PHI). We agree to maintain the confidentiality and security of such information in accordance with applicable laws, including HIPAA.
              </p>

              <h3 className="text-navy fw-semibold" style={{ fontSize: 20, marginBottom: 12, marginTop: 20 }}>5.2 Business Associate Agreement</h3>
              <p style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7, marginBottom: 16 }}>
                As a Business Associate under HIPAA, we will execute a separate Business Associate Agreement (BAA) with you, which supplements these Terms and outlines our specific obligations regarding PHI.
              </p>

              <h3 className="text-navy fw-semibold" style={{ fontSize: 20, marginBottom: 12, marginTop: 20 }}>5.3 Data Security</h3>
              <p style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7 }}>
                We maintain administrative, physical, and technical safeguards designed to protect the confidentiality, integrity, and availability of your data. See our Privacy Policy and HIPAA Compliance Statement for more details.
              </p>
            </section>

            {/* Section 6 */}
            <section style={{ marginBottom: 32 }}>
              <h2 className="text-navy fw-semibold" style={{ fontSize: 24, marginBottom: 16 }}>6. Intellectual Property</h2>
              <h3 className="text-navy fw-semibold" style={{ fontSize: 20, marginBottom: 12, marginTop: 20 }}>6.1 Our Property</h3>
              <p style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7, marginBottom: 16 }}>
                All content, software, technology, reports, and materials provided as part of our Services (except your data) are owned by or licensed to OptiBill Analytics and are protected by intellectual property laws. You may not reproduce, modify, distribute, or create derivative works without our written permission.
              </p>

              <h3 className="text-navy fw-semibold" style={{ fontSize: 20, marginBottom: 12, marginTop: 20 }}>6.2 Your Data</h3>
              <p style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7 }}>
                You retain all rights to your patient data, medical records, and business information. We claim no ownership over your data and will use it solely to provide our Services as authorized by you.
              </p>
            </section>

            {/* Section 7 */}
            <section style={{ marginBottom: 32 }}>
              <h2 className="text-navy fw-semibold" style={{ fontSize: 24, marginBottom: 16 }}>7. Representations and Warranties</h2>
              <h3 className="text-navy fw-semibold" style={{ fontSize: 20, marginBottom: 12, marginTop: 20 }}>7.1 Our Warranties</h3>
              <p style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7, marginBottom: 12 }}>
                We warrant that:
              </p>
              <ul style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7, marginLeft: 24, marginBottom: 16 }}>
                <li>Our Services will be performed in a professional and workmanlike manner</li>
                <li>We will comply with applicable laws and regulations, including HIPAA</li>
                <li>We have the necessary expertise and resources to provide the Services</li>
              </ul>

              <h3 className="text-navy fw-semibold" style={{ fontSize: 20, marginBottom: 12, marginTop: 20 }}>7.2 Disclaimer</h3>
              <p style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7, marginBottom: 12 }}>
                EXCEPT AS EXPRESSLY SET FORTH IN THESE TERMS, OUR SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. WE DISCLAIM ALL IMPLIED WARRANTIES, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
              </p>
              <p style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7 }}>
                We do not guarantee specific reimbursement amounts, collection rates, or financial outcomes. Payment by payers depends on numerous factors outside our control, including medical necessity, coverage policies, and coding accuracy.
              </p>
            </section>

            {/* Section 8 */}
            <section style={{ marginBottom: 32 }}>
              <h2 className="text-navy fw-semibold" style={{ fontSize: 24, marginBottom: 16 }}>8. Limitation of Liability</h2>
              <p style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7, marginBottom: 12 }}>
                TO THE MAXIMUM EXTENT PERMITTED BY LAW:
              </p>
              <ul style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7, marginLeft: 24, marginBottom: 16 }}>
                <li>OUR TOTAL LIABILITY ARISING FROM OR RELATED TO THESE TERMS OR OUR SERVICES SHALL NOT EXCEED THE FEES PAID BY YOU IN THE 12 MONTHS PRECEDING THE EVENT GIVING RISE TO LIABILITY</li>
                <li>WE SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, CONSEQUENTIAL, SPECIAL, OR PUNITIVE DAMAGES, INCLUDING LOST PROFITS OR LOST REVENUE</li>
                <li>WE ARE NOT LIABLE FOR PAYER DENIALS, COVERAGE DETERMINATIONS, OR REIMBURSEMENT DECISIONS</li>
                <li>WE ARE NOT LIABLE FOR DELAYS OR FAILURES CAUSED BY FACTORS BEYOND OUR REASONABLE CONTROL</li>
              </ul>
              <p style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7 }}>
                This limitation applies regardless of the legal theory and whether or not we have been advised of the possibility of such damages.
              </p>
            </section>

            {/* Section 9 */}
            <section style={{ marginBottom: 32 }}>
              <h2 className="text-navy fw-semibold" style={{ fontSize: 24, marginBottom: 16 }}>9. Indemnification</h2>
              <p style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7 }}>
                You agree to indemnify, defend, and hold harmless OptiBill Analytics and its officers, employees, and agents from any claims, damages, losses, or expenses (including reasonable attorneys' fees) arising from: (a) your breach of these Terms, (b) your violation of any law or regulation, (c) your provision of healthcare services, (d) inaccurate or incomplete information provided by you, or (e) any dispute with your patients or third parties.
              </p>
            </section>

            {/* Section 10 */}
            <section style={{ marginBottom: 32 }}>
              <h2 className="text-navy fw-semibold" style={{ fontSize: 24, marginBottom: 16 }}>10. Term and Termination</h2>
              <h3 className="text-navy fw-semibold" style={{ fontSize: 20, marginBottom: 12, marginTop: 20 }}>10.1 Term</h3>
              <p style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7, marginBottom: 16 }}>
                These Terms commence when you first access our Services and continue until terminated as provided herein. Service Agreements may specify additional terms and duration.
              </p>

              <h3 className="text-navy fw-semibold" style={{ fontSize: 20, marginBottom: 12, marginTop: 20 }}>10.2 Termination</h3>
              <p style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7, marginBottom: 12 }}>
                Either party may terminate these Terms:
              </p>
              <ul style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7, marginLeft: 24, marginBottom: 16 }}>
                <li>For convenience with 90 days' written notice</li>
                <li>Immediately for material breach if not cured within 30 days of notice</li>
                <li>Immediately if the other party becomes insolvent or files for bankruptcy</li>
              </ul>

              <h3 className="text-navy fw-semibold" style={{ fontSize: 20, marginBottom: 12, marginTop: 20 }}>10.3 Effect of Termination</h3>
              <p style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7, marginBottom: 12 }}>
                Upon termination:
              </p>
              <ul style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7, marginLeft: 24 }}>
                <li>You must pay all outstanding fees for services rendered</li>
                <li>We will return or destroy your data as requested (subject to legal retention requirements)</li>
                <li>Our confidentiality obligations survive termination</li>
                <li>We will provide reasonable transition assistance for up to 60 days (fees may apply)</li>
              </ul>
            </section>

            {/* Section 11 */}
            <section style={{ marginBottom: 32 }}>
              <h2 className="text-navy fw-semibold" style={{ fontSize: 24, marginBottom: 16 }}>11. Compliance with Laws</h2>
              <p style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7, marginBottom: 12 }}>
                Both parties agree to comply with all applicable federal, state, and local laws and regulations, including:
              </p>
              <ul style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7, marginLeft: 24 }}>
                <li>Health Insurance Portability and Accountability Act (HIPAA)</li>
                <li>False Claims Act and Anti-Kickback Statute</li>
                <li>Stark Law and other self-referral prohibitions</li>
                <li>Medicare and Medicaid regulations</li>
                <li>State medical billing and licensing laws</li>
                <li>Data protection and privacy regulations</li>
              </ul>
            </section>

            {/* Section 12 */}
            <section style={{ marginBottom: 32 }}>
              <h2 className="text-navy fw-semibold" style={{ fontSize: 24, marginBottom: 16 }}>12. Dispute Resolution</h2>
              <h3 className="text-navy fw-semibold" style={{ fontSize: 20, marginBottom: 12, marginTop: 20 }}>12.1 Informal Resolution</h3>
              <p style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7, marginBottom: 16 }}>
                Before pursuing formal dispute resolution, the parties agree to attempt to resolve disputes through good-faith negotiation for at least 30 days.
              </p>

              <h3 className="text-navy fw-semibold" style={{ fontSize: 20, marginBottom: 12, marginTop: 20 }}>12.2 Arbitration</h3>
              <p style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7, marginBottom: 16 }}>
                If informal resolution fails, disputes shall be resolved through binding arbitration in accordance with the rules of the American Arbitration Association, and the decision shall be final and binding.
              </p>

              <h3 className="text-navy fw-semibold" style={{ fontSize: 20, marginBottom: 12, marginTop: 20 }}>12.3 Exceptions</h3>
              <p style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7 }}>
                Either party may seek injunctive relief in court to protect intellectual property rights or confidential information without first pursuing arbitration.
              </p>
            </section>

            {/* Section 13 */}
            <section style={{ marginBottom: 32 }}>
              <h2 className="text-navy fw-semibold" style={{ fontSize: 24, marginBottom: 16 }}>13. General Provisions</h2>
              <ul style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7, marginLeft: 24 }}>
                <li><strong>Entire Agreement:</strong> These Terms, together with any Service Agreement and BAA, constitute the entire agreement between the parties</li>
                <li><strong>Modifications:</strong> We may modify these Terms by posting updated terms on our website with 30 days' notice</li>
                <li><strong>Assignment:</strong> You may not assign these Terms without our written consent. We may assign to affiliates or in connection with a merger or sale</li>
                <li><strong>Severability:</strong> If any provision is found unenforceable, the remaining provisions continue in effect</li>
                <li><strong>Waiver:</strong> Failure to enforce any provision does not waive our right to enforce it later</li>
                <li><strong>Force Majeure:</strong> Neither party is liable for delays caused by events beyond reasonable control</li>
                <li><strong>Governing Law:</strong> These Terms are governed by applicable laws, without regard to conflict of law principles</li>
              </ul>
            </section>

            {/* Section 14 */}
            <section style={{ marginBottom: 32 }}>
              <h2 className="text-navy fw-semibold" style={{ fontSize: 24, marginBottom: 16 }}>14. Contact Information</h2>
              <p style={{ color: '#5A6478', fontSize: 16, lineHeight: 1.7, marginBottom: 12 }}>
                For questions about these Terms or our Services, please contact:
              </p>
              <div className="legal-callout">
                <p style={{ color: '#2B4278', fontSize: 16, marginBottom: 8 }}><strong>OptiBill Analytics</strong></p>
                <p style={{ color: '#5A6478', fontSize: 16, marginBottom: 4 }}>Email: info@optibillanalytics.com</p>
                <p style={{ color: '#5A6478', fontSize: 16, marginBottom: 4 }}>Legal Department: Jayesh Gadhvi</p>
                <p style={{ color: '#5A6478', fontSize: 16 }}>Website: www.optibillanalytics.com</p>
              </div>
            </section>

            {/* Footer Note */}
            <div style={{ marginTop: 40, paddingTop: 24, borderTop: '1px solid rgba(16,35,62,0.12)' }}>
              <p style={{ color: '#5A6478', fontSize: 14, fontStyle: 'italic', textAlign: 'center', marginBottom: 12 }}>
                By using our Services, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
              </p>
              <p style={{ color: '#5A6478', fontSize: 14, fontStyle: 'italic', textAlign: 'center', marginBottom: 12 }}>
                Last Updated: March 21, 2026
              </p>
              <p style={{ color: '#5A6478', fontSize: 13, fontStyle: 'italic', textAlign: 'center', background: '#f7fafc', padding: '12px 16px', borderRadius: 8, border: '1px solid #e6eef7' }}>
                <strong>Legal Disclaimer:</strong> This document does not constitute legal advice. Consult your attorney for specific guidance regarding your situation.
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Terms;
